/**
 * EDL 린터 — 편집 결과에서 **기계가 판정할 수 있는** 결함만 검사하는 순수 함수.
 *
 * 왜 필요한가: AI는 렌더된 영상을 보지도 듣지도 못한 채 "타이밍이 잘 맞습니다"라고
 * 보고한다(실사용 로그). 자기 결과를 확인할 수단이 없으므로, 확인은 코드가 한다.
 *
 * 여기 없는 검사 — 의도적으로 뺐다:
 * - "자막이 발화와 맞는가": 자막을 전사 타임코드에서 생성하므로 둘이 같이 틀린다.
 *   항상 통과하는 항등식이라 방어가 되지 않는다. 실제 오디오를 들어야 잡히는 문제다.
 * - "자막에 없는 말이 있는가": 텍스트가 전사에서 잘려 나오므로 구성상 부분집합이다.
 * - 라우드니스: 렌더된 mp4에서만 측정 가능하다 (다른 실행 컨텍스트).
 */
import { type ClipItem, type Edl, type Item, FPS, framesToSec, itemEnd } from './edl';

export type LintSeverity = 'error' | 'warn';

export interface LintFinding {
  /** 안정적인 코드 — 프롬프트 피드백과 테스트가 이 값을 참조한다. */
  code:
    | 'video_gap'
    | 'video_overlap'
    | 'cut_off_speech'
    | 'clip_too_short'
    | 'caption_overlap'
    | 'caption_past_video'
    | 'sfx_past_video'
    | 'overlay_past_video'
    | 'sfx_present'
    | 'duration_mismatch'
    | 'source_out_of_range'
    | 'empty_timeline';
  severity: LintSeverity;
  /** 모델이 그대로 읽고 고칠 수 있는 한 줄. */
  message: string;
  itemId?: string;
}

/** 발화 구간 (초는 소스 클립 기준). */
export interface LintSpeech {
  start: number;
  end: number;
}

export interface LintOptions {
  /** 발화 경계. 없으면 컷 경계 검사를 건너뛴다 (구 하이라이트). */
  speech?: LintSpeech[] | null;
  /** 컷 경계가 발화 경계에서 이만큼 이내면 통과. 기본 0.15초. */
  cutToleranceSec?: number;
  /** 이보다 짧은 클립은 경고. 기본 1.5초. */
  minClipSec?: number;
  /**
   * 소스 클립의 전체 길이(초). 주면 "손대지 않은 원본"(끝이 소스 끝과 같음)을
   * 컷 경계 검사에서 제외한다 — 원본 끝은 발화 경계일 이유가 없다.
   */
  sourceDurationSec?: number;
}

const DEFAULTS = { cutToleranceSec: 0.15, minClipSec: 1.5 } as const;

function videoClips(edl: Edl): ClipItem[] {
  return edl.tracks
    .filter((t) => t.kind === 'video')
    .flatMap((t) => t.items)
    .filter((i): i is ClipItem => i.type === 'clip')
    .sort((a, b) => a.from - b.from);
}

/** 이 초가 어떤 발화 경계와도 맞지 않으면 true. */
function offSpeechBoundary(sec: number, speech: LintSpeech[], tol: number): boolean {
  return !speech.some((s) => Math.abs(s.start - sec) <= tol || Math.abs(s.end - sec) <= tol);
}

/**
 * 이 초가 어떤 발화 **구간 안쪽**이면 true (= 여기서 자르면 말이 끊긴다).
 * 무음 구간에서 자르는 것은 결함이 아니므로 검사 대상에서 뺀다.
 */
function insideSpeech(sec: number, speech: LintSpeech[], tol: number): boolean {
  return speech.some((s) => sec > s.start + tol && sec < s.end - tol);
}

/**
 * EDL을 검사한다. 반환이 비어 있으면 기계 기준으로는 문제가 없다는 뜻이다.
 * (사람이 봐야 할 것 — 화면 구성, 재미 — 은 여기서 판정하지 않는다.)
 */
export function lintEdl(edl: Edl, opts: LintOptions = {}): LintFinding[] {
  const tol = opts.cutToleranceSec ?? DEFAULTS.cutToleranceSec;
  const minClip = opts.minClipSec ?? DEFAULTS.minClipSec;
  const out: LintFinding[] = [];
  const clips = videoClips(edl);

  if (clips.length === 0) {
    out.push({
      code: 'empty_timeline',
      severity: 'error',
      message: 'video-1 has no clips — the short would render nothing.',
    });
    return out;
  }

  // 1. 갭·겹침. 렌더러는 영상을 clip.from을 무시하고 이어붙이는 반면 오디오·자막은
  //    절대 위치를 쓴다 — 갭이 있으면 소리와 자막만 뒤로 밀린다.
  let cursor = clips[0]!.from;
  if (cursor !== 0) {
    out.push({
      code: 'video_gap',
      severity: 'error',
      message: `the first clip starts at frame ${cursor}, not 0 — move it to 0.`,
      itemId: clips[0]!.id,
    });
  }
  for (const c of clips) {
    if (c.from > cursor) {
      out.push({
        code: 'video_gap',
        severity: 'error',
        message:
          `gap of ${c.from - cursor} frames before clip "${c.id}" — clips must be back-to-back. ` +
          `Move it to frame ${cursor}.`,
        itemId: c.id,
      });
    } else if (c.from < cursor) {
      out.push({
        code: 'video_overlap',
        severity: 'error',
        message: `clip "${c.id}" overlaps the previous clip by ${cursor - c.from} frames.`,
        itemId: c.id,
      });
    }
    cursor = Math.max(cursor, itemEnd(c));
  }

  for (const c of clips) {
    // 2. 타임라인 길이 ↔ 소스 구간 정합성.
    const span = c.source.sourceOutSec - c.source.sourceInSec;
    if (Math.abs(framesToSec(c.durationInFrames) - span) > 1 / FPS) {
      out.push({
        code: 'duration_mismatch',
        severity: 'error',
        message:
          `clip "${c.id}" is ${framesToSec(c.durationInFrames).toFixed(2)}s on the timeline but ` +
          `${span.toFixed(2)}s of source — they must match.`,
        itemId: c.id,
      });
    }

    // 2-1. 소스 범위 초과. 렌더러는 `-t (out-in)`으로 세그먼트를 뽑는데 소스가 그보다
    //      짧으면 **짧은 세그먼트가 나온다** — 영상은 짧고 오디오·자막은 원래 길이 기준으로
    //      배치되어, 뒷부분이 통째로 빈 mp4가 "성공"으로 렌더된다. duration↔소스 정합성
    //      검사는 둘 다 같은 값이라 이걸 못 잡는다.
    if (
      opts.sourceDurationSec !== undefined &&
      c.source.sourceOutSec > opts.sourceDurationSec + 0.05
    ) {
      out.push({
        code: 'source_out_of_range',
        severity: 'error',
        message:
          `clip "${c.id}" reads up to ${c.source.sourceOutSec.toFixed(2)}s but the source is only ` +
          `${opts.sourceDurationSec.toFixed(2)}s — the tail would render black.`,
        itemId: c.id,
      });
    }

    // 3. 너무 짧은 컷 (깜빡임).
    //    severity=error 인 이유: lintFeedback 이 error 만 모델에게 돌려주므로, warn 이면
    //    AI가 0.7초짜리 컷을 만들어도 아무도 말해주지 않는다. 실제 렌더에서 눈에 띄게
    //    깜빡였다. export 차단 목록(FATAL_CODES)에는 넣지 않는다 — 사람이 의도적으로
    //    짧은 컷을 쓸 수 있고, 그건 렌더가 깨지는 문제가 아니다.
    if (framesToSec(c.durationInFrames) < minClip) {
      out.push({
        code: 'clip_too_short',
        severity: 'error',
        message: `clip "${c.id}" is only ${framesToSec(c.durationInFrames).toFixed(2)}s — under ${minClip}s reads as a flicker.`,
        itemId: c.id,
      });
    }

    // 4. 컷 경계가 발화 중간인가 — 가장 눈에 띄는 결함.
    //
    // 검사하지 않는 두 경우:
    // (a) 클립 끝이 소스 전체 길이와 같으면 = 손대지 않은 원본. 원본 끝은 발화 경계일
    //     이유가 없고, 이걸 결함이라 하면 **컷을 요청하지 않은 사용자의 영상을 AI가 자른다.**
    // (b) 경계가 무음 한가운데면 말을 자른 게 아니다. 발화 구간 밖이면 통과시킨다.
    const speech = opts.speech;
    const isUntouchedStart = c.source.sourceInSec <= tol;
    const isUntouchedEnd =
      opts.sourceDurationSec !== undefined && c.source.sourceOutSec >= opts.sourceDurationSec - tol;
    if (speech?.length) {
      if (
        !isUntouchedStart &&
        insideSpeech(c.source.sourceInSec, speech, tol) &&
        offSpeechBoundary(c.source.sourceInSec, speech, tol)
      ) {
        out.push({
          code: 'cut_off_speech',
          severity: 'error',
          message:
            `clip "${c.id}" starts at ${c.source.sourceInSec.toFixed(2)}s, which is mid-utterance. ` +
            `Move it to the nearest speech segment start.`,
          itemId: c.id,
        });
      }
      if (
        !isUntouchedEnd &&
        insideSpeech(c.source.sourceOutSec, speech, tol) &&
        offSpeechBoundary(c.source.sourceOutSec, speech, tol)
      ) {
        out.push({
          code: 'cut_off_speech',
          severity: 'error',
          message:
            `clip "${c.id}" ends at ${c.source.sourceOutSec.toFixed(2)}s, which is mid-utterance. ` +
            `Move it to the nearest speech segment end.`,
          itemId: c.id,
        });
      }
    }
  }

  // 5. 같은 자막 트랙 안에서의 겹침 (렌더에서 서로를 가린다).
  for (const track of edl.tracks.filter((t) => t.kind === 'caption')) {
    const items = [...track.items].sort((a, b) => a.from - b.from);
    for (let i = 0; i < items.length - 1; i++) {
      const a = items[i] as Item;
      const b = items[i + 1] as Item;
      if (itemEnd(a) > b.from) {
        out.push({
          code: 'caption_overlap',
          severity: 'error',
          message: `captions "${a.id}" and "${b.id}" overlap on ${track.id}.`,
          itemId: a.id,
        });
      }
    }
  }

  // 6. 영상 밖에 남은 자막 — 컷을 줄인 뒤 자막이 재생성되지 않으면 생긴다.
  //    `recomputeDuration`이 자막까지 포함해 최대값을 잡으므로 edl.durationInFrames가
  //    부풀고, 그 길이를 기준으로 잡는 브랜딩·CTA 노출 구간이 익스포트 밖으로 밀린다.
  const videoEnd = Math.max(...clips.map((c) => itemEnd(c)));
  const strayCaptions = edl.tracks
    .filter((t) => t.kind === 'caption')
    .flatMap((t) => t.items)
    .filter((i) => i.from >= videoEnd);
  if (strayCaptions.length > 0) {
    out.push({
      code: 'caption_past_video',
      severity: 'error',
      message:
        `${strayCaptions.length} caption(s) start after the last clip ends (frame ${videoEnd}) — ` +
        `they will never be seen and they stretch the project length.`,
      itemId: strayCaptions[0]!.id,
    });
  }

  /*
   * 7. 영상 밖에 남은 효과음 — 6번과 **같은 사고**인데 효과음만 검사에서 빠져 있었다.
   *
   * 컷을 줄여도 sfx 트랙은 아무도 손대지 않는다. 자막은 컷이 바뀔 때마다 서버가
   * 지우고 다시 깔지만(`speechCaptionCommands`) 효과음에는 그런 경로가 없다.
   * 그래서 15초로 자른 프로젝트에 47초·132초짜리 효과음이 그대로 남았고,
   * `recomputeDuration` 이 그 끝을 잡아 프로젝트가 134.9초로 부풀었다
   * (실측 2026-09-11, 프로덕션 프로젝트 e2ecf998).
   *
   * 길이가 부푸는 건 «빈 화면이 붙는다»에서 끝나지 않는다 — CTA 노출 구간은
   * `totalSec - CTA_WINDOW_SEC` 로 잡히므로 **CTA 가 영상이 끝난 뒤 빈 화면에 뜬다.**
   * 즉 결과물에서 CTA 가 영영 안 보인다.
   */
  const straySfx = edl.tracks
    .filter((t) => t.kind === 'sfx')
    .flatMap((t) => t.items)
    .filter((i) => i.from >= videoEnd);
  if (straySfx.length > 0) {
    out.push({
      code: 'sfx_past_video',
      severity: 'error',
      message:
        `${straySfx.length} sfx start after the last clip ends (frame ${videoEnd}) — ` +
        `they will never be heard and they stretch the project length (which pushes the CTA past the end).`,
      itemId: straySfx[0]!.id,
    });
  }

  /*
   * 8. 영상보다 길게 뻗은 «얹힘» 레이어 — broll(웹캠 PIP·컷어웨이)과 overlay(GIF).
   *
   * 6·7번과 병은 같은데 **모양이 다르다.** 저쪽은 영상 뒤에서 «시작»하지만 이건
   * `from=0` 에서 시작해 영상 끝을 지나 계속된다. 그래서 `from >= videoEnd` 검사에
   * 걸리지 않는다 — 실제로 그렇게 빠져나갔다(2026-09-11, 프로젝트 52a983a8:
   * 영상 32초 · 웹캠 PIP 375초 · 프로젝트 375초).
   *
   * 자막과 달리 **걸치는 것도 허용하지 않는다.** 자막이 컷 경계를 조금 넘는 건
   * 글자가 잘리지 않게 하려는 것이고 길이도 몇 프레임 늘 뿐이다. 반면 얹힘 레이어는
   * 그 자체가 화면이라, 영상이 끝난 구간에서는 «영상 없이 PIP 만» 나온다.
   */
  const longLayers = edl.tracks
    .filter((t) => t.kind === 'broll' || t.kind === 'overlay')
    .flatMap((t) => t.items)
    .filter((i) => itemEnd(i) > videoEnd);
  if (longLayers.length > 0) {
    const worst = longLayers.reduce((a, b) => (itemEnd(b) > itemEnd(a) ? b : a));
    out.push({
      code: 'overlay_past_video',
      severity: 'error',
      message:
        `${longLayers.length} overlay/b-roll item(s) run past the last clip (frame ${videoEnd}; ` +
        `furthest ends at ${itemEnd(worst)}) — that stretches the project length and renders ` +
        `frames with no video under them. Trim them to the video's end.`,
      itemId: worst.id,
    });
  }

  return out;
}

/**
 * **렌더가 실제로 깨지는** 결함만. 익스포트를 막는 기준은 이것뿐이다.
 *
 * `cut_off_speech`는 편집 의견이지 파손이 아니다 — 사람이 자른 지점은 정의상 사람의
 * 의도이고, 손으로 끄는 트림은 대부분 발화 한복판에 떨어진다. 이걸로 막으면 수동 편집이
 * 사실상 불가능해진다(3라운드 리뷰: 좌측 트림 위치 1499개 중 1118개가 차단됐다).
 * `caption_overlap`도 사람 소유 트랙에서는 겹쳐 보일 뿐 렌더는 정상이다.
 */
const FATAL_CODE_LIST = [
  'video_gap',
  'video_overlap',
  'duration_mismatch',
  'empty_timeline',
  'caption_past_video',
  'sfx_past_video',
  'overlay_past_video',
  'source_out_of_range',
] as const satisfies readonly LintFinding['code'][];

/**
 * 익스포트를 막는 코드만 따로 타입으로 뽑는다.
 *
 * 🔴 이게 있어야 **한국어 문구를 빼먹을 수 없다.** 여기 있는 메시지는 모델에게
 * 하는 말이라 전부 영문인데, 익스포트가 막히면 그 문장이 사용자 화면에 그대로
 * 뜬다. 실제로 `source_out_of_range` 가 에디터의 문구 표에서 빠져 있어서
 *   `clip "clip-1" runs to 142.30s but the source is 135.00s …`
 * 가 노출될 수 있었다(2026-09-11 확인). 에디터의 표를 `Record<FatalLintCode, …>`
 * 로 묶어 뒀으니, 이 목록에 코드를 더하면 **문구를 안 쓴 쪽이 컴파일 에러로 막힌다.**
 */
export type FatalLintCode = (typeof FATAL_CODE_LIST)[number];

const FATAL_CODES: ReadonlySet<LintFinding['code']> = new Set(FATAL_CODE_LIST);

/** 렌더를 막아야 하는 결함만 추린다. */
export function fatalFindings(findings: LintFinding[]): LintFinding[] {
  return findings.filter((f) => f.severity === 'error' && FATAL_CODES.has(f.code));
}

/** 모델에게 돌려줄 피드백 한 덩어리. error가 없으면 null. */
export function lintFeedback(findings: LintFinding[]): string | null {
  const errors = findings.filter((f) => f.severity === 'error');
  if (errors.length === 0) return null;
  return (
    'The timeline has problems that must be fixed before this is publishable:\n' +
    errors.map((f) => `- [${f.code}] ${f.message}`).join('\n')
  );
}
